// FNP-1378 (REOPENED) — ONE ROW PER REAL ZONE.
//
// The read path fans out across every connection and returns the union, which
// is correct and stays. What was wrong is what the TABLE did with it: three
// hosted zones in one AWS account, read through three connections that assume
// three different roles in that same account, rendered as nine rows and a
// footer reading "9 zones loaded". The account holds three.
//
// The first pass added a Connection column so the rows could be told apart.
// That made the nine rows distinguishable, and a distinguishable wrong count is
// still a wrong count. This groups them.
//
// ─── THE KEY, AND WHY IT IS NOT THE NAME ───
//
// ⚠️ NEVER DE-DUPLICATE BY ZONE NAME. Two providers can each hold a zone for
// the same name, and collapsing them would hide a real split-horizon or a
// genuine misconfiguration — precisely the case `appearsDelegated` exists to
// surface. A name is a label, not an identity.
//
// The key is `provider` + `providerZoneId`, and every part of that was checked
// against the code that produces it rather than assumed:
//
//   flexi-domains-route53-adapter/src/route53-adapter.ts — `listZones` maps
//   every hosted zone through `toNeutralZone`, which sets
//   `providerZoneId: normaliseHostedZoneId(zone.Id)` UNCONDITIONALLY and throws
//   if Route 53 sent no id. So it is populated on the LIST path, not only on
//   the detail path, and it is the bare `Z0123…` form.
//
//   flexi-domains-api/src/dns-read/src/view.ts — `toDnsZoneView` carries it
//   through with `?? null`, and the list fan-out uses that same projection as
//   the single-zone read.
//
//   services/queries.ts — `providerZoneId` is in `DNS_ZONE_FIELDS`, which both
//   operations select.
//
// A Route 53 hosted zone id is unique per AWS account AND per zone, so two
// connections into ONE account report the SAME id for one zone (they merge,
// which is the fix) and two connections into TWO accounts report DIFFERENT ids
// even for an identical name (they stay apart, which is the property that must
// not regress).
//
// `provider` is in the key because the ids are only unique WITHIN a provider.
// Nothing says a Cloudflare zone id can never collide with a Route 53 one, and
// a cross-provider merge would be the exact failure this file exists to
// prevent, arrived at from the other direction.
//
// ─── WHEN THE PROVIDER ISSUES NO ID ───
//
// ⚠️ `providerZoneId` IS OPTIONAL ON THE PORT (`@flexinet/dns-provider-contract`
// declares `readonly providerZoneId?: string`), so an adapter may legitimately
// report none. Route 53 always does, but this module must not be written as
// though Route 53 were the only provider — that is the leak the neutral layer
// exists to prevent.
//
// A row without one falls back to a key built from its OWN `connectionId` and
// `id`, which is the identity the rest of the module already uses. That means
// it merges with nothing and renders as its own row — the pre-fix behaviour,
// for exactly the rows where there is no evidence to merge on. Guessing would
// mean merging on the name, which is the one thing forbidden above.
//
// ⚠️ EMPTY STRING IS A REAL ID, NOT AN ABSENCE. `providerZoneId: ''` is a
// reference the provider chose to issue, and view.ts is explicit that it must
// not be collapsed into null. So this branches on `== null` and never on
// truthiness.
//
// ─── WHEN THE CONNECTIONS DISAGREE ───
//
// Two connections reading one zone can come back with different answers: a
// role with narrower permissions may see a different record count, and
// `appearsDelegated` is computed per read. A grouped row that silently picked
// one of them would be asserting something no connection said — the same
// empty-versus-not-reported confusion `RecordCount` and `NameserverCount`
// already guard against, one level up.
//
// So disagreement is MODELLED, not resolved: `recordCount` and `delegation`
// each carry a tri-state that includes an explicit DISAGREED case, and the
// table renders that case as its own thing rather than as a number.

import {
    delegationStatusOf,
    DnsZone,
    recordCountState,
    ZoneDelegationStatus,
} from './types';

/**
 * The identity of a real zone, as a string this module groups on.
 *
 * ⚠️ THE `provider` PREFIX IS LOAD-BEARING and so is the separator. Two ids
 * from two providers must never collide, and a separator that could appear
 * inside a provider id would let `('ROUTE53', 'A B')` and `('ROUTE53 A', 'B')`
 * produce one string. A space cannot appear in a provider identifier.
 */
export const zoneIdentityKey = (zone: DnsZone): string => {
    // ⚠️ `== null` and NOT a truthiness test. An empty string is a real id
    // that a provider chose to issue; treating it as absent would split one
    // zone into one row per connection, which is the defect this file fixes.
    if (zone.providerZoneId == null) {
        // No provider id to merge on. Fall back to this row's OWN identity so
        // it groups with nothing — never with a same-named row, which is the
        // merge this module forbids.
        return `NO_PROVIDER_ID ${zone.connectionId} ${zone.id}`;
    }
    return `${zone.provider} ${zone.providerZoneId}`;
};

/**
 * What the grouped row can say about the record count.
 *
 * ⚠️ THREE STATES, AND THE THIRD IS THE NEW ONE. `COUNTED` and `NOT_REPORTED`
 * are `RecordCountState` unchanged; `DISAGREED` is what a group means when its
 * connections returned different numbers. Rendering that as either number
 * would be the row asserting something no connection said.
 */
export type GroupedRecordCount =
    | {kind: 'COUNTED', count: number}
    | {kind: 'NOT_REPORTED'}
    | {kind: 'DISAGREED', counts: number[]};

/**
 * What the grouped row can say about delegation.
 *
 * ⚠️ FNP-1423 — THIS NOW CARRIES `ZoneDelegationStatus`, THE API'S OWN ANSWER,
 * rather than the `appearsDelegated` tri-state it was built on. The two ask
 * different questions: `appearsDelegated` compares two facts the platform
 * stores, while `delegationStatus` asks the delegation chain what it actually
 * yields over public DNS. The second is the one an operator can act on.
 *
 * `NOT_DETERMINED` already means "no conclusion was reached"; `DISAGREED` means
 * conclusions were reached and they differ, which is a stronger and more
 * actionable statement.
 *
 * ⚠️ THE REASON RIDES ALONG on the agreed case. A NOT_DETERMINED with no
 * explanation is the bare "unknown" the API side went to trouble to avoid, and
 * a group that dropped it on the way through would reintroduce exactly that.
 */
export type GroupedDelegation =
    | {kind: 'AGREED', status: ZoneDelegationStatus, reason?: string | null}
    | {kind: 'DISAGREED', statuses: ZoneDelegationStatus[]};

/**
 * One real zone, and every connection that can reach it.
 */
export type GroupedZone = {
    /** `zoneIdentityKey` of the rows below. The table's `idAccessor`. */
    key: string,
    /**
     * The row this group is REPRESENTED BY — the first in fetch order.
     *
     * ⚠️ NOT A MERGED OBJECT. A synthetic zone stitched together from several
     * reads would be a shape no connection actually returned, and every
     * consumer downstream (the detail link, the delete call, the selection
     * slice) would be acting on it. This is a real row, kept whole, and the
     * fields that can DISAGREE are surfaced separately below rather than being
     * silently taken from it.
     */
    representative: DnsZone,
    /**
     * Every row that grouped here, in fetch order. Length 1 is the ordinary
     * case: one connection reaches this zone.
     */
    zones: DnsZone[],
    /** The connection ids that reached this zone, de-duplicated, in fetch order. */
    connectionIds: string[],
    /** The agreed record count, or the fact that the connections disagreed. */
    recordCount: GroupedRecordCount,
    /** The agreed delegation state, or the fact that the connections disagreed. */
    delegation: GroupedDelegation,
};

/**
 * Reduce the record counts several connections reported for one zone.
 *
 * ⚠️ A REPORTED COUNT BEATS A MISSING ONE, and that is not the same as
 * disagreement. One connection answering `7` while another answers nothing is
 * not two conflicting claims — it is one claim and one silence, and `7` is the
 * only thing anybody said. Only two DIFFERENT NUMBERS are a disagreement.
 *
 * ⚠️ `0` IS A NUMBER HERE, exactly as it is in `recordCountState`. A zone one
 * connection counted as empty and another did not count at all reports 0.
 */
const reduceRecordCount = (zones: DnsZone[]): GroupedRecordCount => {

    const counts: number[] = [];

    zones.forEach(zone => {
        const state = recordCountState(zone);
        if (state.kind === 'COUNTED' && !counts.includes(state.count)) {
            counts.push(state.count);
        }
    });

    if (counts.length === 0) {
        // Nobody reported one. NOT "zero records" — see RecordCount.
        return {kind: 'NOT_REPORTED'};
    }

    if (counts.length === 1) {
        return {kind: 'COUNTED', count: counts[0]};
    }

    return {kind: 'DISAGREED', counts};
};

/**
 * Reduce the delegation conclusions several connections reached for one zone.
 *
 * ⚠️ SAME ASYMMETRY AS THE COUNT, FOR THE SAME REASON. `UNDETERMINED` is an
 * absence of a conclusion, not a competing one, so it loses to any connection
 * that actually reached one. Two connections that both reached conclusions and
 * reached DIFFERENT ones is the real disagreement.
 *
 * A group where nobody concluded anything is `UNDETERMINED`, which is what
 * every row of the dev estate reports today.
 */
const reduceDelegation = (zones: DnsZone[]): GroupedDelegation => {

    // ⚠️ NOT_DETERMINED IS THE ONLY MEMBER THAT LOSES TO ANOTHER, and the line
    // between it and PARTIAL is the one worth being careful about. PARTIAL is a
    // REAL CONCLUSION — the parent's nameserver set overlaps this zone's but
    // disagrees, a half-applied delegation change — so it competes on equal
    // terms with DELEGATED and NOT_DELEGATED. Only NOT_DETERMINED is an absence
    // of a verdict, and only it yields.
    const determined: ZoneDelegationStatus[] = [];

    zones.forEach(zone => {
        const status = delegationStatusOf(zone);
        if (status !== 'NOT_DETERMINED' && !determined.includes(status)) {
            determined.push(status);
        }
    });

    if (determined.length === 0) {
        // Nobody reached a verdict. ⚠️ Carry the REASON from whichever
        // connection supplied one — dropping it here would turn the API's
        // considered "why" into the bare "unknown" it exists to replace. The
        // first non-empty wins: the connections are reading one real zone, so a
        // second reason would say the same thing in the same words.
        const explained = zones.find(
            zone => typeof zone.delegationUndeterminedReason === 'string'
                && zone.delegationUndeterminedReason !== '',
        );
        return {
            kind: 'AGREED',
            status: 'NOT_DETERMINED',
            reason: explained?.delegationUndeterminedReason ?? null,
        };
    }

    if (determined.length === 1) {
        // A verdict every connection that reached one agreed on. No reason:
        // `delegationUndeterminedReason` is null on every status but
        // NOT_DETERMINED, by the schema's own contract.
        return {kind: 'AGREED', status: determined[0]};
    }

    return {kind: 'DISAGREED', statuses: determined};
};

/**
 * Group zone rows by the real zone they describe.
 *
 * ⚠️ FETCH ORDER IS PRESERVED, both between groups and inside one. The caller
 * sorts afterwards, and a grouping that reordered rows would make the sort
 * comparator's tie-breaking non-deterministic between renders.
 *
 * ⚠️ THIS NEVER DROPS A ROW. Every input zone appears in exactly one group's
 * `zones`, so the union the fan-out returned is still fully represented — the
 * table shows fewer ROWS, not less information. That distinction is what keeps
 * the fan-out honest: nothing became invisible, it became one row that names
 * every connection behind it.
 */
export const groupZonesByIdentity = (zones: DnsZone[]): GroupedZone[] => {

    const byKey = new Map<string, DnsZone[]>();

    zones.forEach(zone => {
        const key = zoneIdentityKey(zone);
        const existing = byKey.get(key);
        if (existing) {
            existing.push(zone);
        } else {
            // A Map preserves insertion order, which is how fetch order
            // survives grouping without a separate sort.
            byKey.set(key, [zone]);
        }
    });

    return Array.from(byKey.entries())
                .map(([key, grouped]) => ({
                    key,
                    representative: grouped[0],
                    zones: grouped,
                    // De-duplicated: one connection could in principle report a
                    // zone twice across pages, and the row must not then claim
                    // two connections reach it.
                    connectionIds: Array.from(
                        new Set(grouped.map(zone => zone.connectionId)),
                    ),
                    recordCount: reduceRecordCount(grouped),
                    delegation: reduceDelegation(grouped),
                }));
};
